import type { PaginationDto, PaginationResponseDto } from "core/dto";
import { ORDER_BY } from "core/interfaces";
import type { PageMetaDto } from "shared/dto";

import { getPaginationDefault } from "~/common/defaults/pagination.default";
import { getPaginationResponseDefault } from "~/common/defaults/pagination-response.default";
import { getTagDefault } from "~/common/defaults/tag.default";
import type { IdDto } from "~/common/interfaces/dto/id.dto";
import type { CreateTagDto } from "~/common/interfaces/dto/tags/create-tag.dto";
import type { GetAllTagsDto } from "~/common/interfaces/dto/tags/get-all-tags.dto";
import type { UpdateTagByIdDto } from "~/common/interfaces/dto/tags/update-tag-by-id.dto";
import type { Tag } from "~/common/interfaces/entities/tag.interface";
import type { Sort } from "~/common/interfaces/sort.interface";

export const useTagsStore = defineStore(
	"tags",
	() => {
		const currentTag = ref<Tag>(getTagDefault());
		const tags = ref<Tag[]>([]);
		const meta = ref<PageMetaDto>(getPaginationResponseDefault());
		const recentGetAllQuery = ref<GetAllTagsDto & PaginationDto>();
		const tableSort = ref<Sort>({
			columns: ["name"],
			orderBy: ["name", ORDER_BY.ASC]
		});

		const createOne = async (dto: CreateTagDto): Promise<Tag> => {
			const tag = await useNuxtApp().$apiFetch<Tag>("/tags", {
				method: "POST",
				body: dto
			});

			return tag;
		};
		const getAll = async (
			dto: GetAllTagsDto = {},
			paginationDto?: Partial<PaginationDto>
		): Promise<PaginationResponseDto<Tag>> => {
			const [column, orderBy] = tableSort.value.orderBy;

			recentGetAllQuery.value = {
				...getPaginationDefault(),
				...paginationDto,
				...dto,
				column,
				orderBy
			};

			const res = await useNuxtApp().$apiFetch<PaginationResponseDto<Tag>>("/tags", {
				query: recentGetAllQuery.value
			});
			tags.value = res.data;
			meta.value = res.meta;

			return res;
		};
		const getById = (id: number): Promise<Tag> => {
			return useNuxtApp().$apiFetch<Tag>(`/tags/${id}`);
		};
		const patchById = (id: number, dto: UpdateTagByIdDto): Promise<Tag> => {
			return useNuxtApp().$apiFetch<Tag>(`/tags/${id}`, {
				method: "PATCH",
				body: dto
			});
		};
		const deleteById = async (id: number): Promise<IdDto> => {
			const res = await useNuxtApp().$apiFetch<IdDto>(`/tags/${id}`, {
				method: "DELETE"
			});

			return res;
		};

		return {
			currentTag,
			tags,
			meta,
			recentGetAllQuery,
			tableSort,

			createOne,
			getAll,
			getById,
			patchById,
			deleteById
		};
	},
	{
		persist: {
			paths: ["tableSort.orderBy"]
		}
	}
);
